<script>
/**
 * WhatsappTemplates.vue
 *
 * WhatsApp template management page.
 *
 * Views:
 *   list   — filterable table of cached templates with sync + create actions
 *   create — WhatsAppTemplateForm with live preview, toggled inline
 *
 * Template preview opens in a woot-modal when a row is clicked.
 */
import { useAlert } from 'dashboard/composables';
import whatsappTemplatesAPI from 'dashboard/api/whatsappTemplates';
import WhatsAppTemplateForm from 'dashboard/components/widgets/whatsapp/WhatsAppTemplateForm.vue';
import WhatsAppTemplatePreview from 'dashboard/components/widgets/whatsapp/WhatsAppTemplatePreview.vue';

export default {
  name: 'WhatsappTemplates',

  components: { WhatsAppTemplateForm, WhatsAppTemplatePreview },

  props: {
    inboxId: {
      type: [Number, String],
      required: true,
    },
  },

  data() {
    return {
      // view state
      view: 'list', // 'list' | 'create'
      saving: false,
      loading: false,
      syncing: false,

      // template data
      templates: [],
      lastSynced: null,

      // list filters
      searchQuery: '',
      statusFilter: '',
      categoryFilter: '',

      // preview modal
      previewTemplate: null,
      showPreviewModal: false,
    };
  },

  computed: {
    accountId() {
      return this.$route.params.accountId;
    },

    filteredTemplates() {
      let list = this.templates;

      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        list = list.filter(t => t.name?.toLowerCase().includes(q));
      }

      if (this.statusFilter) {
        list = list.filter(
          t => (t.status || '').toUpperCase() === this.statusFilter
        );
      }

      if (this.categoryFilter) {
        list = list.filter(
          t => (t.category || '').toUpperCase() === this.categoryFilter
        );
      }

      return list;
    },

    statusCounts() {
      const counts = { APPROVED: 0, PENDING: 0, REJECTED: 0 };
      this.templates.forEach(t => {
        const s = (t.status || 'PENDING').toUpperCase();
        if (s in counts) counts[s] += 1;
      });
      return counts;
    },

    // Props for the preview modal
    previewHeaderType() {
      return this.getTemplateHeaderType(this.previewTemplate);
    },

    previewHeaderContent() {
      if (!this.previewTemplate) return '';
      const header = this.getTemplateHeader(this.previewTemplate);
      return header?.text || header?.example?.header_handle?.[0] || '';
    },

    previewBody() {
      return this.getTemplateBody(this.previewTemplate) || '';
    },

    previewFooter() {
      return this.getTemplateFooter(this.previewTemplate) || '';
    },

    previewButtons() {
      return this.getTemplateButtons(this.previewTemplate) || [];
    },
  },

  mounted() {
    this.fetchTemplates();
  },

  methods: {
    // ── Data helpers ──────────────────────────────────────────

    getTemplateBody(template) {
      if (!template?.components) return '';
      const c = template.components.find(x => x.type === 'BODY');
      return c?.text || '';
    },

    getTemplateHeader(template) {
      if (!template?.components) return null;
      return template.components.find(x => x.type === 'HEADER') || null;
    },

    getTemplateFooter(template) {
      if (!template?.components) return '';
      const c = template.components.find(x => x.type === 'FOOTER');
      return c?.text || '';
    },

    getTemplateButtons(template) {
      if (!template?.components) return [];
      const c = template.components.find(x => x.type === 'BUTTONS');
      return c?.buttons || [];
    },

    getTemplateHeaderType(template) {
      const header = this.getTemplateHeader(template);
      if (!header) return 'none';
      const fmt = (header.format || '').toUpperCase();
      if (fmt === 'TEXT') return 'text';
      if (fmt === 'IMAGE') return 'image';
      if (fmt === 'VIDEO') return 'video';
      if (fmt === 'DOCUMENT') return 'document';
      return 'none';
    },

    // ── API calls ─────────────────────────────────────────────

    async fetchTemplates() {
      this.loading = true;
      try {
        const { data } = await whatsappTemplatesAPI.list(
          this.accountId,
          this.inboxId
        );
        this.templates = data.templates || [];
        this.lastSynced = data.last_synced || null;
      } catch {
        useAlert('Erro ao buscar templates');
      } finally {
        this.loading = false;
      }
    },

    async syncTemplates() {
      this.syncing = true;
      try {
        const { data } = await whatsappTemplatesAPI.sync(
          this.accountId,
          this.inboxId
        );
        this.templates = data.templates || [];
        this.lastSynced = data.last_synced || null;
        useAlert('Templates sincronizados com sucesso');
      } catch {
        useAlert('Erro ao sincronizar templates');
      } finally {
        this.syncing = false;
      }
    },

    async handleFormSubmit(payload, mediaFile = null) {
      this.saving = true;
      try {
        await whatsappTemplatesAPI.create(this.accountId, this.inboxId, payload, mediaFile);
        useAlert('Template enviado para aprovacao. Aguarde a revisao da Meta.');
        this.view = 'list';
        // Delay sync to give Meta time to register the new template
        setTimeout(() => this.syncTemplates(), 3000);
      } catch (err) {
        useAlert(
          err?.response?.data?.error || 'Erro ao criar template'
        );
      } finally {
        this.saving = false;
      }
    },

    // ── View actions ──────────────────────────────────────────

    openCreate() {
      this.view = 'create';
    },

    closeCreate() {
      this.view = 'list';
    },

    openPreview(template) {
      this.previewTemplate = template;
      this.showPreviewModal = true;
    },

    closePreview() {
      this.showPreviewModal = false;
      this.previewTemplate = null;
    },

    // ── Display helpers ───────────────────────────────────────

    formatDate(dateStr) {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    statusClass(status) {
      switch ((status || '').toUpperCase()) {
        case 'APPROVED':
          return 'wt-badge wt-badge--approved';
        case 'REJECTED':
          return 'wt-badge wt-badge--rejected';
        default:
          return 'wt-badge wt-badge--pending';
      }
    },

    statusLabel(status) {
      switch ((status || '').toUpperCase()) {
        case 'APPROVED': return 'Aprovado';
        case 'REJECTED': return 'Rejeitado';
        case 'PENDING':  return 'Pendente';
        default:         return status || 'Pendente';
      }
    },

    categoryLabel(cat) {
      switch ((cat || '').toUpperCase()) {
        case 'MARKETING':      return 'Marketing';
        case 'UTILITY':        return 'Utilidade';
        case 'AUTHENTICATION': return 'Autenticacao';
        default:               return cat || '-';
      }
    },

    toggleStatus(value) {
      this.statusFilter = this.statusFilter === value ? '' : value;
    },
  },
};
</script>

<template>
  <div class="wt-root">

    <!-- ══════════════════════════════════════════════════════════
         LIST VIEW
    ═══════════════════════════════════════════════════════════ -->
    <template v-if="view === 'list'">

      <!-- Header -->
      <div class="wt-header">
        <div>
          <h2 class="wt-title">Templates WhatsApp</h2>
          <p class="wt-subtitle">
            {{ templates.length }} template{{ templates.length !== 1 ? 's' : '' }}
            <span v-if="lastSynced"> &middot; Sync: {{ formatDate(lastSynced) }}</span>
          </p>
        </div>
        <div class="wt-header-actions">
          <button
            class="wt-btn wt-btn--secondary"
            :disabled="syncing"
            @click="syncTemplates"
          >
            <span
              class="size-4"
              :class="syncing ? 'i-ri-loader-4-line wt-spin' : 'i-ri-refresh-line'"
            />
            {{ syncing ? 'Sincronizando...' : 'Sincronizar' }}
          </button>
          <button class="wt-btn wt-btn--primary" @click="openCreate">
            <span class="i-ri-add-line size-4" />
            Criar Template
          </button>
        </div>
      </div>

      <!-- Status pills -->
      <div class="wt-pills">
        <button
          class="wt-pill"
          :class="statusFilter === '' ? 'wt-pill--active' : ''"
          @click="statusFilter = ''"
        >
          Todos ({{ templates.length }})
        </button>
        <button
          class="wt-pill wt-pill--approved"
          :class="statusFilter === 'APPROVED' ? 'wt-pill--approved-active' : ''"
          @click="toggleStatus('APPROVED')"
        >
          Aprovados ({{ statusCounts.APPROVED }})
        </button>
        <button
          class="wt-pill wt-pill--pending"
          :class="statusFilter === 'PENDING' ? 'wt-pill--pending-active' : ''"
          @click="toggleStatus('PENDING')"
        >
          Pendentes ({{ statusCounts.PENDING }})
        </button>
        <button
          class="wt-pill wt-pill--rejected"
          :class="statusFilter === 'REJECTED' ? 'wt-pill--rejected-active' : ''"
          @click="toggleStatus('REJECTED')"
        >
          Rejeitados ({{ statusCounts.REJECTED }})
        </button>
      </div>

      <!-- Filters row -->
      <div class="wt-filters">
        <div class="wt-search-wrapper">
          <span class="i-ri-search-line size-4 wt-search-icon" />
          <input
            v-model="searchQuery"
            type="text"
            class="wt-search"
            placeholder="Buscar por nome..."
          />
        </div>
        <select v-model="categoryFilter" class="wt-select">
          <option value="">Todas as categorias</option>
          <option value="MARKETING">Marketing</option>
          <option value="UTILITY">Utilidade</option>
          <option value="AUTHENTICATION">Autenticacao</option>
        </select>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="wt-state-center">
        <span class="i-ri-loader-4-line size-6 wt-spin wt-text-muted" />
        <span class="wt-text-muted">Carregando templates...</span>
      </div>

      <!-- Empty state -->
      <div
        v-else-if="!filteredTemplates.length"
        class="wt-state-center"
      >
        <span class="i-ri-inbox-line size-8 wt-text-muted" />
        <p class="wt-text-muted">
          {{ templates.length === 0 ? 'Nenhum template encontrado. Clique em Sincronizar para carregar.' : 'Nenhum template corresponde aos filtros.' }}
        </p>
      </div>

      <!-- Table -->
      <div v-else class="wt-table-wrapper">
        <table class="wt-table">
          <thead>
            <tr>
              <th class="wt-th">Nome</th>
              <th class="wt-th">Categoria</th>
              <th class="wt-th">Idioma</th>
              <th class="wt-th">Status</th>
              <th class="wt-th">Ultima atualizacao</th>
              <th class="wt-th wt-th--action" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="template in filteredTemplates"
              :key="template.name"
              class="wt-tr"
              @click="openPreview(template)"
            >
              <td class="wt-td">
                <span class="wt-template-name">{{ template.name }}</span>
                <span class="wt-template-body">{{ getTemplateBody(template) }}</span>
              </td>
              <td class="wt-td">
                <span class="wt-badge wt-badge--category">
                  {{ categoryLabel(template.category) }}
                </span>
              </td>
              <td class="wt-td wt-text-mono">{{ template.language || '-' }}</td>
              <td class="wt-td">
                <span :class="statusClass(template.status)">
                  {{ statusLabel(template.status) }}
                </span>
              </td>
              <td class="wt-td wt-text-muted">
                {{ formatDate(template.last_updated || template.updated_at) }}
              </td>
              <td class="wt-td wt-td--action" @click.stop>
                <button
                  class="wt-icon-btn"
                  title="Ver previa"
                  @click="openPreview(template)"
                >
                  <span class="i-ri-eye-line size-4" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

    </template>

    <!-- ══════════════════════════════════════════════════════════
         CREATE VIEW
    ═══════════════════════════════════════════════════════════ -->
    <template v-else-if="view === 'create'">

      <!-- Sub-header -->
      <div class="wt-header">
        <div class="wt-back-heading">
          <button class="wt-icon-btn" title="Voltar" @click="closeCreate">
            <span class="i-ri-arrow-left-line size-5" />
          </button>
          <div>
            <h2 class="wt-title">Novo Template</h2>
            <p class="wt-subtitle">Crie e envie para aprovacao da Meta</p>
          </div>
        </div>
      </div>

      <WhatsAppTemplateForm
        :saving="saving"
        @submit="handleFormSubmit"
        @cancel="closeCreate"
      />

    </template>

    <!-- ══════════════════════════════════════════════════════════
         PREVIEW MODAL
    ═══════════════════════════════════════════════════════════ -->
    <woot-modal
      v-model:show="showPreviewModal"
      :on-close="closePreview"
    >
      <div v-if="previewTemplate" class="wt-modal-inner">
        <!-- Modal header -->
        <div class="wt-modal-header">
          <div>
            <h3 class="wt-modal-title">{{ previewTemplate.name }}</h3>
            <div class="wt-modal-meta">
              <span :class="statusClass(previewTemplate.status)">
                {{ statusLabel(previewTemplate.status) }}
              </span>
              <span class="wt-badge wt-badge--category">
                {{ categoryLabel(previewTemplate.category) }}
              </span>
              <span class="wt-text-mono wt-text-muted">{{ previewTemplate.language }}</span>
            </div>
          </div>
        </div>

        <!-- Preview component -->
        <div class="wt-modal-preview">
          <WhatsAppTemplatePreview
            :header-type="previewHeaderType"
            :header-content="previewHeaderContent"
            :body="previewBody"
            :footer="previewFooter"
            :buttons="previewButtons"
          />
        </div>

        <!-- Body text (raw) for reference -->
        <div v-if="previewBody" class="wt-modal-body-ref">
          <p class="wt-modal-body-label">Corpo da mensagem</p>
          <p class="wt-modal-body-text">{{ previewBody }}</p>
        </div>
      </div>
    </woot-modal>

  </div>
</template>

<style scoped lang="scss">
.wt-root {
  @apply text-n-slate-12;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  font-size: 0.875rem;
}

.wt-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.wt-title {
  @apply text-n-slate-12;
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
}

.wt-subtitle {
  @apply text-n-slate-11;
  font-size: 0.8125rem;
  margin: 0.125rem 0 0;
}

.wt-header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.wt-back-heading {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.wt-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.4375rem 0.875rem;
  font-size: 0.8125rem;
  font-weight: 500;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s;
  white-space: nowrap;
}

.wt-btn--primary {
  @apply bg-n-brand text-white;

  &:hover:not(:disabled) {
    @apply opacity-90;
  }
}

.wt-btn--secondary {
  @apply bg-n-slate-3 text-n-slate-12 dark:bg-n-solid-3;

  &:hover:not(:disabled) {
    @apply bg-n-slate-4 dark:bg-n-solid-2;
  }
}

.wt-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.wt-icon-btn {
  @apply text-n-slate-11;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 0.375rem;
  background: transparent;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    @apply bg-n-slate-3 text-n-slate-12 dark:bg-n-solid-3;
  }
}

.wt-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.wt-pill {
  @apply bg-n-slate-3 text-n-slate-11 dark:bg-n-solid-3;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.wt-pill--active {
  @apply bg-n-slate-12 text-n-slate-1;
}

.wt-pill--approved {
  @apply bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300;
}

.wt-pill--approved-active {
  @apply bg-green-600 text-white;
}

.wt-pill--pending {
  @apply bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300;
}

.wt-pill--pending-active {
  @apply bg-yellow-600 text-white;
}

.wt-pill--rejected {
  @apply bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300;
}

.wt-pill--rejected-active {
  @apply bg-red-600 text-white;
}

.wt-filters {
  display: flex;
  gap: 0.625rem;
  flex-wrap: wrap;
}

.wt-search-wrapper {
  position: relative;
  flex: 1 1 200px;
  min-width: 0;
}

.wt-search-icon {
  @apply text-n-slate-10;
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  display: inline-block;
}

.wt-search {
  @apply bg-n-surface-1 border border-n-weak text-n-slate-12;
  width: 100%;
  padding: 0.4375rem 0.625rem 0.4375rem 2.25rem;
  font-size: 0.875rem;
  border-radius: 0.5rem;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s;

  &:focus {
    @apply border-n-blue-9;
  }
}

.wt-select {
  @apply bg-n-surface-1 border border-n-weak text-n-slate-12;
  padding: 0.4375rem 0.625rem;
  font-size: 0.875rem;
  border-radius: 0.5rem;
  outline: none;
  cursor: pointer;
  transition: border-color 0.15s;

  &:focus {
    @apply border-n-blue-9;
  }
}

.wt-state-center {
  @apply text-n-slate-11;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 3rem 1rem;
}

.wt-table-wrapper {
  @apply border border-n-weak;
  overflow-x: auto;
  border-radius: 0.75rem;
}

.wt-table {
  width: 100%;
  border-collapse: collapse;
}

.wt-th {
  @apply bg-n-slate-2 text-n-slate-11 border-b border-n-weak dark:bg-n-solid-2;
  padding: 0.625rem 0.875rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.wt-th--action {
  width: 2.5rem;
}

.wt-tr {
  cursor: pointer;
  transition: background 0.1s;

  &:hover {
    @apply bg-n-slate-2 dark:bg-n-solid-2;
  }

  &:not(:last-child) .wt-td {
    @apply border-b border-n-weak;
  }
}

.wt-td {
  padding: 0.75rem 0.875rem;
  vertical-align: middle;
}

.wt-td--action {
  width: 2.5rem;
  text-align: center;
}

.wt-template-name {
  @apply text-n-slate-12;
  display: block;
  font-weight: 600;
  font-size: 0.8125rem;
}

.wt-template-body {
  @apply text-n-slate-11;
  display: block;
  font-size: 0.75rem;
  max-width: 28ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 0.125rem;
}

.wt-badge {
  display: inline-block;
  padding: 0.1875rem 0.5rem;
  font-size: 0.6875rem;
  font-weight: 600;
  border-radius: 9999px;
  line-height: 1.4;
  white-space: nowrap;
}

.wt-badge--approved {
  @apply bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300;
}

.wt-badge--pending {
  @apply bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300;
}

.wt-badge--rejected {
  @apply bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300;
}

.wt-badge--category {
  @apply bg-n-slate-3 text-n-slate-11 dark:bg-n-solid-3;
}

.wt-text-muted {
  @apply text-n-slate-11;
}

.wt-text-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.75rem;
}

@keyframes wt-spin {
  to { transform: rotate(360deg); }
}

.wt-spin {
  animation: wt-spin 0.8s linear infinite;
}

.wt-modal-inner {
  padding: 1.25rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.wt-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.wt-modal-title {
  @apply text-n-slate-12;
  font-size: 0.9375rem;
  font-weight: 700;
  margin: 0 0 0.375rem;
}

.wt-modal-meta {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;
}

.wt-modal-preview {
  display: flex;
  justify-content: center;
  padding: 0.5rem 0;
}

.wt-modal-body-ref {
  @apply bg-n-slate-2 border border-n-weak dark:bg-n-solid-2;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
}

.wt-modal-body-label {
  @apply text-n-slate-11;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0 0 0.375rem;
}

.wt-modal-body-text {
  @apply text-n-slate-12;
  font-size: 0.875rem;
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
